import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router';
import { Spacer, TextTitle } from '@pypestream/design-system';
import { StudioPageProps } from '../hooks/use-pro-studio-routes';
import { WithLoadingState } from '../router';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { getChannelsFromPypes } from '../utils';
import { VoiceChannelSettingsForm } from '../components';
import { VoiceChannelFormValues } from '../components/voice-channel-settings-form/voice-channel-settings-form';
import {
  resolveInitialPreFluxSilence,
  resolveVoiceChannelInitialValues,
} from '../components/voice-channel-settings-form/voice-channel-initial-values';

type VoiceChannelSettingsPageProps = StudioPageProps;

const VoiceChannelSettingsPage: React.FC<
  VoiceChannelSettingsPageProps
> = () => {
  const { channelId } = useParams();
  const location = useLocation();
  const isUpdating = !!channelId && !location.pathname.includes('/create');

  const { botPypes } = useProStudioCtxSelector((ctx) => ({
    botPypes: ctx.botPypes || [],
  }));

  const selectedChannel = useMemo(
    () =>
      isUpdating
        ? getChannelsFromPypes(botPypes).find(
            ({ id, type }) => id === channelId && type === 'twilio_voice'
          )
        : null,
    [botPypes, channelId, isUpdating]
  );

  const initialValues: VoiceChannelFormValues = useMemo(
    () => resolveVoiceChannelInitialValues(selectedChannel),
    [selectedChannel]
  );

  return (
    <>
      <TextTitle test-id="ps-voice-channel-settings-title" size="small">
        {isUpdating ? 'Channel Settings' : 'Create Voice Channel'}
      </TextTitle>
      <Spacer size="large" />
      <VoiceChannelSettingsForm
        type={isUpdating ? 'update' : 'create'}
        initialValues={initialValues}
        initialPreFluxSilence={resolveInitialPreFluxSilence(selectedChannel)}
        channelId={channelId}
      />
    </>
  );
};

export const VoiceChannelSettings = WithLoadingState(VoiceChannelSettingsPage);
